import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { type IntegrationParamOption } from './common';
import { zeusApiV1 } from 'src/store/api';

// TODO - PAYF-14646: move shared IntegrationData + getIntegrationData to something like
// shared/api/integrations/integration.ts — they are used by all integrations,
// not only Factorial.

export type ConnectArgs = { apiKey: string; subsidiary: string | undefined };

export type SaveArgs = {
  data?: Record<string, unknown>;
  id: string;
  subsidiary: string;
  legalEntityId: string | undefined;
  teams?: string[];
  leaves?: string[];
  customFields?: Record<string, unknown> | null;
};

export type IntegrationData = {
  id: string;
  credentials: {
    apiKey: string;
    code?: string;
    clientId: string;
    clientSecret: string;
    baseURL?: string;
    appName?: string;
    appToken?: string;
    urlPrefix?: string;
    country?: string;
  };
  company: string;
  fieldMap?: { fieldMapping: Record<string, any>; fieldEquivalences: any };
  flowsEnabled: any;
  parameters: {
    recordTransactions?: boolean;
    teams: string[];
    leaves: string[];
    legalEntityId: string;
    companyCode?: string;
    includeSalaryTypes?: boolean;
    subcompany?: string;
    departments?: string[];
    offices?: string[];
    leaveTypes?: string[];
    idEmpresa?: string;
    idConcepto?: string;
    company_id?: number;
    conceptCode?: number;
    urlPrefix?: string;
    country?: string;
  };
  legalEntityId: string;
  software: string;
  subsidiary: string;
};

export type FactorialTeam = {
  avatar: string | null;
  company_id: string;
  description: string | null;
  employee_ids: string[];
  id: string;
  lead_ids: string[];
  name: 'string';
};

export type FactorialLeave = {
  translated_name: string;
  approved: boolean;
  created_at: string;
  description: string | null;
  employee_full_name: string;
  employee_id: string;
  finish_on: string;
  id: string;
  leave_type_id: string;
  leave_type_name: string;
  start_on: string;
  updated_at: string;
};

export type FactorialLegalEntity = {
  address_line_1: string;
  address_line_2: string;
  city: string | null;
  company_id: string;
  country: string;
  currency: string;
  id: string;
  legal_name: string;
  postal_code: string;
  state: string;
  tax_identification_number: string;
};

export type FactorialParamsResponse = {
  teams: FactorialTeam[];
  leaves: FactorialLeave[];
  legalEntities: FactorialLegalEntity[];
};

export type FactorialCustomFieldsResponse = IntegrationParamOption[];

type GetIntegrationArgs = {
  subsidiary: string;
};

const extendedApi = zeusApiV1.injectEndpoints({
  endpoints: build => ({
    connect: build.mutation<ConnectArgs, any>({
      query: ({ apiKey, subsidiary }) => ({
        url: `/hercules/v1/factorial/credentials`,
        method: 'POST',
        body: { apiKey, subsidiary }
      }),
      invalidatesTags: ['factorial']
    }),
    getFactorialParams: build.query<FactorialParamsResponse, GetIntegrationArgs>({
      queryFn: async ({ subsidiary }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: `/hercules/v1/factorial/company-params`,
            params: { subsidiary }
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as FactorialParamsResponse };
        } catch (error) {
          console.error('Error fetching data:', error);
          throw error;
        }
      },
      providesTags: ['factorial']
    }),
    getFactorialCustomFields: build.query<FactorialCustomFieldsResponse, GetIntegrationArgs>({
      queryFn: async ({ subsidiary }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: `/hercules/v1/factorial/custom-fields`,
            params: { subsidiary }
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as FactorialCustomFieldsResponse };
        } catch (error) {
          console.error('Error fetching data:', error);
          throw error;
        }
      },
      providesTags: ['factorial']
    }),
    save: build.mutation<SaveArgs, any>({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const res = await fetchWithBQ({
          url: `/hercules/v1/factorial/config`,
          method: 'PATCH',
          body: args
        });

        return { data: res as any };
      },
      invalidatesTags: ['integration']
    }),
    getIntegrationData: build.query<IntegrationData[], void>({
      query: () => `/hercules/v1/integration`,
      providesTags: ['factorial', 'integration']
    }),
    getFactorialIntegrationUrl: build.query<string, GetIntegrationArgs>({
      queryFn: async ({ subsidiary }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/factorial/oauth',
            params: {
              subsidiary
            }
          });

          if (result.error) {
            return { error: result.error };
          }

          const url = (result.data as { url: string }).url;
          globalThis.location.replace(url);

          return { data: url };
        } catch (error) {
          console.error('Error fetching integration URL:', error);

          return { error: error as FetchBaseQueryError };
        }
      }
    })
  }),
  overrideExisting: false
});

export const {
  useConnectMutation,
  useGetFactorialParamsQuery,
  useGetFactorialCustomFieldsQuery,
  useSaveMutation,
  useGetIntegrationDataQuery,
  useLazyGetIntegrationDataQuery,
  useLazyGetFactorialIntegrationUrlQuery
} = extendedApi;
